import { useEffect, useRef, useState } from 'react';
import { useIsPhone } from '../events/EventsPage';
import { DialogLayer } from '../ui/Dialog';

/** "Pobierz PDF": the server names the file (`kosztorys-<data>-<wydarzenie>-v<N>.pdf`); a sent version gives its stored PDF. */
export function downloadPdf(versionId: string): Promise<void> {
  return downloadFrom(`/api/quotes/versions/${versionId}/pdf`);
}

/** The file name of `Content-Disposition` (`filename*` in UTF-8 first, then `filename`); null when none. */
function fileNameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star) {
    try {
      return decodeURIComponent(star[1]!.trim());
    } catch {
      // fall back to the plain name
    }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? null;
}

/**
 * Downloads the file of `url` under the name the server gives it. Review I2 (owner 2026-11-04 22:36): fetched first, so a
 * refusal (e.g. 422 „Uzupełnij ceny pozycji własnych: …”) rejects with the server's message instead of saving an error.
 */
export async function downloadFrom(url: string): Promise<void> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let message = 'Nie udało się pobrać pliku';
    try {
      const body = (await res.json()) as { message?: unknown };
      if (typeof body.message === 'string' && body.message) message = body.message;
    } catch {
      // not JSON: the plain message
    }
    throw new Error(message);
  }
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = fileNameOf(res.headers.get('content-disposition')) ?? 'dokument.pdf';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/** The PDF of a version of „Oferta łączona” (wzór F, 2026-10-31). */
export const offerPdfUrl = (offerVersionId: string): string => `/api/quotes/offer-versions/${offerVersionId}/pdf`;

/**
 * "Podgląd PDF" (P8, owner points 8/26): the version's PDF in a window over the quote — a draft rendered now and
 * marked "PROJEKT", a sent version its stored file. Nothing is saved, sent or changed. On a phone the window fills
 * the screen. Safari on iPhone may show only the first page in a frame: "Otwórz w nowej karcie" shows all of it.
 * "Zamknij" takes the focus; closing ("Zamknij" or Escape, `DialogLayer`) gives focus back to what opened it (e.g.
 * "Podgląd PDF" in "Wyślij do klienta"), so a next Escape closes that dialog, not the event card.
 */
export function PdfPreviewDialog({ versionId, number, onDownload, onClose, offer = false, downloadBlocked = null }: {
  versionId: string;
  number: number;
  /** May reject with the server's message (review I2): it is shown in the window. */
  onDownload: () => void | Promise<void>;
  onClose: () => void;
  /** „Oferta łączona” (2026-10-31): the offer version's PDF, no print view */
  offer?: boolean;
  /** Owner 2026-11-04 22:36: why „Pobierz PDF” is off (prices still to give); the preview itself stays */
  downloadBlocked?: string | null;
}) {
  const phone = useIsPhone();
  const close = useRef<HTMLButtonElement>(null);
  const src = offer ? `${offerPdfUrl(versionId)}?inline=1` : `/api/quotes/versions/${versionId}/pdf?inline=1`;
  useEffect(() => close.current?.focus(), []);
  const [downloadError, setDownloadError] = useState('');
  const download = async () => {
    setDownloadError('');
    try {
      await onDownload();
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'Nie udało się pobrać PDF');
    }
  };
  return (
    <div className="dialog-backdrop">
      <div className={`dialog panel q-pdf-preview${phone ? ' full' : ''}`} role="dialog" aria-modal="true" aria-labelledby="pdf-preview-title" data-feature-id="quotes.pdf_preview">
        <div className="q-pdf-preview-head">
          <h2 id="pdf-preview-title">Podgląd PDF · v{number}</h2>
          <div className="actions">
            <a className="btn small ghost" href={src} target="_blank" rel="noopener">
              Otwórz w nowej karcie
            </a>
            <button className="btn small" type="button" onClick={() => void download()} disabled={!!downloadBlocked} title={downloadBlocked ?? undefined}>
              Pobierz PDF
            </button>
            {/* wzór F (owner 2026-10-30): the same version in the print view */}
            {!offer && (
              <a className="btn small ghost" href={`/wydruk/kosztorys/${versionId}`} data-feature-id="quotes.print_view">
                Podgląd wydruku
              </a>
            )}
            <DialogLayer onClose={onClose} />
            <button ref={close} className="btn small primary" type="button" onClick={onClose}>
              Zamknij
            </button>
          </div>
        </div>
        {downloadError && (
          <p className="alert" role="alert">
            {downloadError}
          </p>
        )}
        <iframe className="q-pdf-frame" src={src} title={`${offer ? 'PDF oferty łączonej' : 'PDF kosztorysu'}, wersja ${number}`} />
      </div>
    </div>
  );
}
